'use client';

import React, { useState } from 'react';
import { Search, SlidersHorizontal, X } from 'lucide-react';

export type ActionType =
  | 'all' |'GRN_CREATED' |'ITEM_ADDED' |'QC_APPROVED' |'QC_REJECTED' |'RACK_SLOTTED' |'ITEM_PICKED' |'KIT_COMPLETED' |'DISPATCH';

export interface AuditFilters {
  operator: string;
  dateFrom: string;
  dateTo: string;
  actionType: ActionType;
  search: string;
}

interface Props {
  filters: AuditFilters;
  onChange: (f: AuditFilters) => void;
  onReset: () => void;
}

const ACTION_OPTIONS: { value: ActionType; label: string }[] = [
  { value: 'all', label: 'All Actions' },
  { value: 'GRN_CREATED', label: 'GRN Created' },
  { value: 'ITEM_ADDED', label: 'Item Added' },
  { value: 'QC_APPROVED', label: 'QC Approved' },
  { value: 'QC_REJECTED', label: 'QC Rejected' },
  { value: 'RACK_SLOTTED', label: 'Rack Slotted' },
  { value: 'ITEM_PICKED', label: 'Item Picked' },
  { value: 'KIT_COMPLETED', label: 'Kit Completed' },
  { value: 'DISPATCH', label: 'Dispatch' },
];

const OPERATORS = ['All Operators', 'Prasun Sharma', 'Ravi Kumar', 'Anita Desai', 'Suresh Patel', 'Meena Joshi'];

export default function AuditTrailFiltersBar({ filters, onChange, onReset }: Props) {
  const [showFilters, setShowFilters] = useState(true);

  const hasActive =
    filters.operator !== 'All Operators' ||
    filters.dateFrom !== '' ||
    filters.dateTo !== '' ||
    filters.actionType !== 'all' ||
    filters.search !== '';

  return (
    <div className="bg-card border border-border rounded-xl p-4 space-y-3">
      {/* Top row: search + toggle */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search item, barcode, GRN, rack…"
            value={filters.search}
            onChange={(e) => onChange({ ...filters, search: e.target.value })}
            className="w-full pl-8 pr-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
          />
        </div>
        <button
          onClick={() => setShowFilters((v) => !v)}
          className={`flex items-center gap-1.5 px-3 py-2 rounded-lg border text-xs font-medium transition-colors ${
            showFilters
              ? 'border-primary/40 bg-primary/10 text-primary' :'border-border text-muted-foreground hover:text-foreground hover:bg-muted'
          }`}
        >
          <SlidersHorizontal size={13} />
          Filters
        </button>
        {hasActive && (
          <button
            onClick={onReset}
            className="flex items-center gap-1 px-2.5 py-2 rounded-lg border border-border text-xs text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          >
            <X size={12} />
            Clear
          </button>
        )}
      </div>

      {/* Filter row */}
      {showFilters && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {/* Operator */}
          <div className="flex flex-col gap-1">
            <label className="text-2xs font-semibold uppercase tracking-wider text-muted-foreground">Operator</label>
            <select
              value={filters.operator}
              onChange={(e) => onChange({ ...filters, operator: e.target.value })}
              className="px-2.5 py-2 bg-background border border-border rounded-lg text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
            >
              {OPERATORS.map((op) => (
                <option key={op} value={op}>{op}</option>
              ))}
            </select>
          </div>

          {/* Date From */}
          <div className="flex flex-col gap-1">
            <label className="text-2xs font-semibold uppercase tracking-wider text-muted-foreground">From Date</label>
            <input
              type="date"
              value={filters.dateFrom}
              onChange={(e) => onChange({ ...filters, dateFrom: e.target.value })}
              className="px-2.5 py-2 bg-background border border-border rounded-lg text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
            />
          </div>

          {/* Date To */}
          <div className="flex flex-col gap-1">
            <label className="text-2xs font-semibold uppercase tracking-wider text-muted-foreground">To Date</label>
            <input
              type="date"
              value={filters.dateTo}
              onChange={(e) => onChange({ ...filters, dateTo: e.target.value })}
              className="px-2.5 py-2 bg-background border border-border rounded-lg text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
            />
          </div>

          {/* Action Type */}
          <div className="flex flex-col gap-1">
            <label className="text-2xs font-semibold uppercase tracking-wider text-muted-foreground">Action Type</label>
            <select
              value={filters.actionType}
              onChange={(e) => onChange({ ...filters, actionType: e.target.value as ActionType })}
              className="px-2.5 py-2 bg-background border border-border rounded-lg text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
            >
              {ACTION_OPTIONS.map((a) => (
                <option key={a.value} value={a.value}>{a.label}</option>
              ))}
            </select>
          </div>
        </div>
      )}
    </div>
  );
}
